import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { PublicTournamentDataService } from '../../services/publicTournamentData';

type Filter = 'all' | 'upcoming' | 'past';

const toTournamentDay = (value?: string | Date | null) => {
  if (!value) return null;
  return typeof value === 'string' ? dayjs(value.slice(0, 10)) : dayjs(value);
};

const getPlainText = (value?: string | null) => value?.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim() || '';

export function FriendlyEventList() {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<Filter>('upcoming');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const locale = typeof navigator !== 'undefined' ? navigator.language : 'en-US';

  useEffect(() => {
    const id = window.setTimeout(() => setDebouncedSearch(search), 300);
    return () => window.clearTimeout(id);
  }, [search]);

  const { data, isLoading } = useQuery({
    queryKey: ['events-list', filter, debouncedSearch],
    queryFn: PublicTournamentDataService.getTournamentList,
  });

  const tournaments = data || [];
  const eventCounts = useMemo(() => tournaments.reduce((counts, tournament) => {
    const eventDay = toTournamentDay(tournament.endDate ?? tournament.date);
    if (!eventDay || !eventDay.isBefore(dayjs().startOf('day'))) counts.upcoming += 1;
    else counts.past += 1;
    return counts;
  }, { upcoming: 0, past: 0 }), [tournaments]);

  const filtered = useMemo(() => {
    const today = dayjs().startOf('day');
    const query = search.trim().toLowerCase();
    return tournaments.filter((tournament) => {
      const eventDay = toTournamentDay(tournament.endDate ?? tournament.date);
      if (filter === 'upcoming' && eventDay?.isBefore(today)) return false;
      if (filter === 'past' && eventDay && !eventDay.isBefore(today)) return false;
      if (!query) return true;
      return `${tournament.name} ${tournament.location} ${tournament.description}`.toLowerCase().includes(query);
    });
  }, [filter, search, tournaments]);

  const dateParts = (value?: string | Date | null) => {
    if (!value) return { day: '—', month: 'TBA', formatted: t('eventDetail.fallbacks.dateTba') };
    const date = dayjs(value).toDate();
    return {
      day: new Intl.DateTimeFormat(locale, { day: '2-digit' }).format(date),
      month: new Intl.DateTimeFormat(locale, { month: 'short' }).format(date),
      formatted: new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric', year: 'numeric' }).format(date),
    };
  };

  return (
    <div className="page-stack">
      <section className="page-intro">
        <div className="page-intro__copy"><p className="eyebrow">{t('eventsList.heroEyebrow')}</p><h1 className="page-title">{t('eventsList.title')}</h1><p className="lead">{t('eventsList.heroCopy')}</p></div>
        <div className="count-bubbles">
          <div className="count-bubble"><span>{t('eventsList.filters.upcoming')}</span><strong>{eventCounts.upcoming}</strong></div>
          <div className="count-bubble"><span>{t('eventsList.filters.past')}</span><strong>{eventCounts.past}</strong></div>
        </div>
      </section>

      <section className="filter-panel" aria-label={t('eventsList.searchLabel')}>
        <div className="filter-pills">
          {(['upcoming', 'all', 'past'] as Filter[]).map((option) => <button type="button" className={`filter-pill ${filter === option ? 'is-active' : ''}`} onClick={() => setFilter(option)} key={option}>{t(`eventsList.filters.${option}`)}</button>)}
        </div>
        <input className="field-search" type="search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder={t('eventsList.searchPlaceholder')} aria-label={t('eventsList.searchLabel')} />
      </section>

      {isLoading ? <div className="event-list">{Array.from({ length: 5 }).map((_, index) => <div className="skeleton" key={index} />)}</div> : filtered.length ? (
        <div className="event-list">
          {filtered.map((event) => {
            const date = dateParts(event.date);
            const description = getPlainText(event.description);
            return <Link className="event-list__row" to={`/events/${event.id}`} key={event.id}>
              <div className="event-list__date"><div><strong>{date.day}</strong><span>{date.month}</span></div></div>
              <div className="event-list__copy"><h3>{event.name}</h3>{description && <p>{description}</p>}</div>
              <div className="event-list__place"><strong>{event.location || t('homeUpcoming.locationTba')}</strong><div>{[date.formatted, event.endDate && !dayjs(event.endDate).isSame(event.date, 'day') ? dateParts(event.endDate).formatted : null].filter(Boolean).join(' – ')}</div></div>
              <span className="circle-arrow" aria-hidden="true">→</span>
            </Link>;
          })}
        </div>
      ) : <div className="empty-state">{t('eventsList.empty')}</div>}
    </div>
  );
}
